/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.22.0
 */

import { FC, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ERequestStatus } from '@/main/types/globalTypes';
import { LinkBlock } from '@components/common';
import { makeSelectPageReportsConstructorTranslation, makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { Loader } from '@modules/loader/components/Loader';
import Modal from '@modules/modal/components/Modal';
import { modalSetStatusAction } from '@modules/modal/modalActions';
import { EModalStatus } from '@modules/modal/types/modalTypes';
import ButtonServiceUserDenied from '@modules/role/components/ButtonServiceUserDenied';

import { useRemoveScheduleMutation } from '../../store';
import { TReportId } from '../../types';
import { ConstructorRequestStatusModal } from '../ConstructorRequestStatusModal';

export interface IProps {
    cppoId: number;
    reportId: TReportId;
    onCloseModal: () => void;
};

export const ScheduleRemoveModal: FC<IProps> = ({ cppoId, reportId, onCloseModal }) => {
    const dispatch = useDispatch();

    const { modal: tModal } = useSelector(makeSelectTranslationCommon());
    const {
        scheduleTable: tSchedule,
        failure: tFailure,
    } = useSelector(makeSelectPageReportsConstructorTranslation());

    const [
        deleteSchedule,
        {
            error,
            isError,
            isLoading,
            isSuccess,
        },
    ] = useRemoveScheduleMutation();

    const handleScheduleRemove = () => {
        deleteSchedule({ cppoId, reportId });
    };

    useEffect(() => {
        if (isSuccess) {
            onCloseModal();
        }
    }, [isSuccess, onCloseModal]);

    useEffect(() => {
        if (isError) {
            dispatch(modalSetStatusAction({
                status: EModalStatus.failure,
            }));
        }
    }, [dispatch, isError]);

    return (
        <Modal
            dataName='schedule-remove-modal'
            style={{ padding: '24px' }}
            width='400px'
        >
            <Modal.Status status={EModalStatus.start}>
                <Modal.Title className='modal-popup-title__base'>
                    {tModal.attention}
                </Modal.Title>
                <Loader showLoader={isLoading}>
                    <Modal.Text>
                        {tSchedule.modalRemoveMessage}
                    </Modal.Text>
                    <Modal.Control className='modal-popup-control__base'>
                        <ButtonServiceUserDenied
                            onClick={handleScheduleRemove}
                        >
                            {tModal.remove}
                        </ButtonServiceUserDenied>
                        <LinkBlock
                            className='basket__footer-link'
                            onClick={onCloseModal}
                        >
                            {tModal.close}
                        </LinkBlock>
                    </Modal.Control>
                </Loader>
            </Modal.Status>
            <Modal.Status status={EModalStatus.failure}>
                <ConstructorRequestStatusModal
                    mode={ERequestStatus.fail}
                    requestError={error}
                    title={tFailure.removeScheduleModalTitle}
                    onCloseModal={onCloseModal}
                />
            </Modal.Status>
        </Modal >
    );
};
